import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { EMAIL_MESSAGES, EmailLanguageSchema, ForgotPasswordRequestSchema, ResetPasswordRequestSchema, } from './email.js';
import { PaidModuleSchema } from './ops.js';
import { isPermission } from './permissions.js';
import { IsoDateSchema } from './primitives.js';
const c = initContract();
/** One of the 60 `<area>.<action>` strings of 16.2.2 — nothing else passes. */
const PermissionSchema = z.custom((value) => typeof value === 'string' && isPermission(value));
/**
 * The auth surface: GEN-S1 (sign in), GEN-S2 (sign up = tenant creation),
 * sign-out, and the demo protected read `/api/me`.
 *
 * Error wording is part of the contract. The spec fixes the exact sentences
 * (BR-GEN-S1-02/03/04, ERR-GEN-S2-01), and the single most important property
 * of the sign-in failure message is that it does NOT say which field was
 * wrong — so the strings live here as constants the UI reuses rather than
 * retypes.
 */
export const AUTH_MESSAGES = {
    /**
     * BR-GEN-S1-02 / ERR-GEN-S1-01 — one message for any credential failure,
     * never which half. The error table's "Typed a wrong password" row and the
     * business rule fix the same sentence from two sides; this constant is both.
     */
    badCredentials: 'That email and password do not match. Try again.',
    /** BR-GEN-S1-03 / ERR-GEN-S1-03. */
    lockedOut: 'Too many tries. Try again in 15 minutes.',
    /** BR-GEN-S1-04 / ERR-GEN-S1-04. */
    accountDisabled: 'This account is switched off. Ask your administrator.',
    /**
     * BR-OPS-01-08: a suspended company's users "must be told that the company's
     * access is suspended and pointed at their own Owner. The message must not
     * name the operator, must not blame the tenant, and must not hint at a
     * reason the operator did not publish." Every clause of that shapes this one
     * sentence — note what it does not say.
     */
    tenantSuspended: "Your company's access is suspended. Please contact your account Owner.",
    /**
     * R21: "On expiry: sign-in is blocked immediately (data retained, never
     * deleted; the operator extends to restore)."
     *
     * The sentence is shaped by what it must NOT do. It does not name a date, a
     * price, a plan, an invoice or an amount — the person reading it may be an
     * AP clerk who has no business knowing what their company pays, and leaking
     * billing detail to every seat is a real disclosure. It does not say
     * "unpaid" or "overdue", because a lapsed period is not an accusation and
     * the operator may simply not have got round to extending it. And it points
     * at US, not at the company's own Owner — unlike a suspension, this is not
     * something anyone inside the company can fix.
     */
    subscriptionExpired: "Your company's subscription has ended, so sign-in is closed. Your data is safe and nothing has been deleted. Please contact your vendor to restore access.",
    /** ERR-GEN-S2-01 / BR-GEN-S2-02. */
    emailTaken: 'That email already has an account. Sign in instead.',
    /** Generic 401 for a protected route with no (or an expired) session. */
    notSignedIn: 'You are not signed in.',
    /** Signup burst throttle (the basic in-app limiter; Turnstile at deploy). */
    tooManySignups: 'Too many sign-ups from this connection. Try again later.',
    /**
     * The recovery flow's four sentences live in EMAIL_MESSAGES, where the rest
     * of the mail vocabulary is, and are re-exported here so a sign-in screen
     * does not have to know that "forgot password" is an email concern.
     */
    resetRequested: EMAIL_MESSAGES.resetRequested,
    resetLinkInvalid: EMAIL_MESSAGES.resetLinkInvalid,
    resetRateLimited: EMAIL_MESSAGES.resetRateLimited,
    verificationLinkInvalid: EMAIL_MESSAGES.verificationLinkInvalid,
    alreadyVerified: EMAIL_MESSAGES.alreadyVerified,
    emailNotVerified: EMAIL_MESSAGES.emailNotVerified,
};
/** Stored lowercased; sign-in must match regardless of the case typed. */
const EmailSchema = z
    .email()
    .max(320)
    .transform((value) => value.toLowerCase());
/**
 * GEN-S1 §5 / GEN-S2: minimum 10 characters with at least one letter and one
 * number (the spec's proposed default, adopted); "accept up to 200 characters,
 * ignore anything beyond" — hence the slice rather than a max() rejection.
 */
const NewPasswordSchema = z
    .string()
    .transform((value) => value.slice(0, 200))
    .pipe(z
    .string()
    .min(10, 'Password must be at least 10 characters.')
    .regex(/[\p{L}]/u, 'Password must contain at least one letter.')
    .regex(/[0-9]/, 'Password must contain at least one number.'));
const MessageSchema = z.object({ message: z.string() }).strict();
export const SignUpRequestSchema = z
    .object({
    /** Becomes the first user — the account carrying the owner flag (R18). */
    fullName: z.string().trim().min(1).max(120),
    workEmail: EmailSchema,
    /**
     * Becomes the tenant name (BR-GEN-S2-07). Arabic accepted and stored.
     * `trim().min(1)` is ERR-GEN-S2-05's enforcement: a company name that is
     * empty or only spaces is refused — on the page through the resolver,
     * which renders the spec's exact sentence ("Type your company name.",
     * `auth.errCompanyEmpty`), and here again for any caller that is not the
     * page.
     */
    companyName: z.string().trim().min(1).max(120),
    /**
     * Deviation from GEN-S2, recorded: the spec sets the password via an
     * activation email (BR-GEN-S2-08). The API takes the password directly and
     * sends the activation email alongside it; whether the activation link is
     * REQUIRED before the first sign-in is the REQUIRE_EMAIL_VERIFICATION
     * switch, off until a real provider is configured (TODO(deploy)). Plan/card
     * fields are billing scope and absent (BR-GEN-S2-03 holds trivially).
     */
    password: NewPasswordSchema,
    /**
     * The language every email to this person is written in. It comes from the
     * language switcher on the signup screen the person is standing on, which
     * is the one moment the sender and the recipient are the same human — so
     * "the recipient's language" is knowable here and nowhere else on this
     * path. Stored on the user row; the company's other invitations inherit it.
     */
    language: EmailLanguageSchema.default('en'),
})
    .strict();
export const SignUpResponseSchema = z
    .object({
    userId: z.uuid(),
    tenantId: z.uuid(),
    tenantSlug: z.string(),
    /**
     * R21 — WHETHER THE COMPANY CAN ACTUALLY BE USED YET.
     *
     * Signing up creates the company. It does not, on its own, buy access:
     * R21 removed the fixed trial-entitlement set that used to be granted
     * silently here, so a self-signup now reaches the product only through
     * route (a), an online card payment, and that route cannot complete until
     * a payment provider is wired (DECISION.md §8.7).
     *
     * The flag is on the response rather than inferred by the screen because
     * the screen must not guess. `true` and the new Owner is signed straight
     * in; `false` and they are told to contact the vendor — and the company,
     * with everything in it, is waiting for the operator to grant a period.
     * Nothing is lost either way, which is the half of R21 that says data is
     * retained and never deleted.
     */
    subscriptionActive: z.boolean(),
    /** Present only when `subscriptionActive` is false — what to do about it. */
    message: z.string().optional(),
})
    .strict();
export const SignInRequestSchema = z
    .object({
    email: EmailSchema,
    /** Verification only — the stored hash caps what matters; no policy here. */
    password: z.string().min(1).transform((value) => value.slice(0, 200)),
    /**
     * GEN-S1 "Remember me": true → 30-day persistent cookie; false → session
     * cookie the browser drops on close (the spec's proposed defaults).
     */
    rememberMe: z.boolean().default(false),
})
    .strict();
export const SignInResponseSchema = z
    .object({
    userId: z.uuid(),
    tenantId: z.uuid(),
})
    .strict();
export const MeResponseSchema = z
    .object({
    user: z
        .object({
        id: z.uuid(),
        email: z.string(),
        name: z.string(),
        status: z.enum(['invited', 'active', 'suspended', 'deactivated']),
        /** Ruling R18 — a flag on the account, not a role. */
        isOwner: z.boolean(),
    })
        .strict(),
    tenant: z
        .object({
        id: z.uuid(),
        name: z.string(),
        slug: z.string(),
    })
        .strict(),
    /**
     * The session's effective permissions, resolved exactly as the guard
     * resolves them: the ACTIVE role's grant rows, sorted. The shape is kept
     * honest for the two accounts outside the 16.2.2 vocabulary: the OWNER's
     * list is EMPTY — the isOwner flag above IS the grant (BR-PERM-66: "no
     * switch is consulted", so none is reported, never 60 strings) — and a
     * role-less user's list is empty because near-zero holds nothing
     * (BR-PERM-73/74). Fresh on every request, like the guard (OQ-16-14).
     */
    permissions: PermissionSchema.array(),
    /**
     * THE PAID MODULES THIS COMPANY ACTUALLY HAS, resolved the way
     * EntitlementsGuard resolves them: a live subscription period AND an
     * enabled `tenant_entitlements` row, both read inside one transaction on
     * this request. A module absent from this list is one the company never
     * bought or no longer holds.
     *
     * It is here because §1.5.3 asks the interface to make a module ABSENT —
     * "the whole Manufacturing / Production menu group, and its four screens"
     * disappear — and until now the browser had no way to know. The list was
     * the one fact the shell needed and did not have, so it drew Manufacturing
     * for companies that had never bought it.
     *
     * WHAT IT IS NOT. It is not a permission and it is not a gate. The guard
     * is the gate (BR-OPS-02-04, BR-PERM-51: "Navigation filtering is a
     * display convenience… A route must never rely on its menu entry being
     * hidden"), and it stays exactly as it was. This list only lets the menu
     * stop lying. Anyone who forges it changes what they SEE and nothing they
     * may DO.
     *
     * Fresh on every request, like `permissions`, and for the same reason
     * 17.5.3 gives: "Caching it beyond the request is how a toggle stops
     * taking effect."
     */
    modules: PaidModuleSchema.array(),
    /**
     * THE COMPANY'S TODAY (N-16 / EC-19-65), and the reason it is here rather
     * than worked out in the browser: `interface.default_date` says a new
     * document opens on "today", and until now the SPA answered that with
     * `new Date()` on the reader's own machine. A laptop with a wrong clock,
     * or simply one in a different time zone from the company, then dated a
     * real posting — the last place a user's machine decided a business date.
     *
     * `today` is resolved by CompanyClock inside the request, in the tenant's
     * own `fiscal.timezone`. Only the answer travels, not the zone: a client
     * that could recompute the date would be trusting the clock this exists
     * to stop trusting.
     */
    clock: z.object({ today: IsoDateSchema }).strict(),
    /**
     * WHAT THIS COMPANY'S MONEY IS (GEN-107: "exactly one currency marked as
     * the base currency"), read from `currencies.is_base` on every request —
     * here for the same reason `clock.today` is, and answering the same shape
     * of question.
     *
     * `interface.default_date` made the browser decide a business date;
     * `<Amount currency="USD">` made it decide what a figure was DENOMINATED
     * IN, at sixty-odd sites across POS, the inventory and sales dashboards,
     * the four report screens and the receipt/delivery/adjustment lists.
     * Every one of those figures is a ledger or stock valuation, which
     * GEN-107 puts in the base currency and nothing else; the literal simply
     * printed `$` beside a right number for every company that is not
     * dollar-based.
     *
     * WHAT IT IS NOT. It is not a conversion, and no screen that takes it may
     * present it as one: GEN-115's disclosure belongs to screens that CONVERT
     * (the cash-flow consolidation), and GEN-116 hides it when nothing was
     * converted — which is the case for every reader of this field. Naming
     * the money a figure is already in is a label, not an arithmetic claim.
     *
     * A screen showing a DOCUMENT's own money still reads the document
     * (GEN-108) — an invoice in euros says euros whatever the base is. This
     * answers only "what does this company report in".
     */
    baseCurrency: z.string(),
})
    .strict();
/**
 * Note what is NOT in any request shape: a tenant id. The tenant comes from
 * the session and only from the session (DECISION.md §5.7 — client input
 * never selects a control path). The generated isolation suite iterates this
 * contract, which is why every operation must be mounted here.
 */
export const authContract = c.router({
    signUp: {
        method: 'POST',
        path: '/api/auth/sign-up',
        body: SignUpRequestSchema,
        responses: {
            201: SignUpResponseSchema,
            409: MessageSchema,
            429: MessageSchema,
        },
        summary: 'GEN-S2: create tenant + owner user in one transaction. Public; rate limited per IP /64.',
    },
    signIn: {
        method: 'POST',
        path: '/api/auth/sign-in',
        body: SignInRequestSchema,
        responses: {
            200: SignInResponseSchema,
            401: MessageSchema,
            403: MessageSchema,
            429: MessageSchema,
        },
        summary: 'GEN-S1: verify credentials, issue the session cookie, write the audit row. Public; lockout after 5 failures.',
    },
    /**
     * GEN-S1 "Forgot password?" — the half of the flow the spec leaves as
     * "[NOT IN PROTOTYPE — decide at build time]".
     *
     * ALWAYS 200, whatever the address turns out to be. DECISION.md §5.16 is
     * explicit — "a generic 200 always" and "never leak account existence
     * through the limiter" — so an unknown address, a suspended account and a
     * real one are indistinguishable from outside, including in timing:
     * nothing about the answer depends on what was found.
     *
     * The token's life is 15 MINUTES, from §5.16 ("a 15-minute single-use
     * token"), not the 60 minutes GEN-S1 §5 floats as a proposed default. The
     * security ruling is the binding one and the difference is surfaced to the
     * owner rather than split.
     */
    forgotPassword: {
        method: 'POST',
        path: '/api/auth/forgot-password',
        body: ForgotPasswordRequestSchema,
        responses: {
            200: MessageSchema,
            429: MessageSchema,
        },
        summary: 'GEN-S1 recovery, step 1: queue a reset email. Public; ALWAYS 200 (no existence oracle); 5/hr per /64 and 3/hr per address.',
    },
    resetPassword: {
        method: 'POST',
        path: '/api/auth/reset-password',
        body: ResetPasswordRequestSchema,
        responses: {
            200: MessageSchema,
            /** Expired, already used, or the account is no longer signable-in. */
            410: MessageSchema,
            429: MessageSchema,
        },
        summary: 'GEN-S1 recovery, step 2: set the new password from a single-use 15-minute token. Public; every session of that account dies with it.',
    },
    /**
     * BR-GEN-S2-08/09 — the activation link from the signup email. Public by
     * necessity: the whole point is that the person is not signed in yet.
     */
    verifyEmail: {
        method: 'POST',
        path: '/api/auth/verify-email',
        body: z.object({ token: z.string().min(3).max(200) }).strict(),
        responses: {
            200: MessageSchema,
            410: MessageSchema,
        },
        summary: 'BR-GEN-S2-08/09: confirm an email address from the activation link. Public; the link lives 72 hours and is single-use.',
    },
    signOut: {
        method: 'POST',
        path: '/api/auth/sign-out',
        body: z.object({}).strict().optional(),
        responses: {
            200: z.object({ ok: z.literal(true) }).strict(),
            401: MessageSchema,
        },
        summary: 'Revoke the current session and clear its cookie.',
    },
    me: {
        method: 'GET',
        path: '/api/me',
        responses: {
            200: MeResponseSchema,
            401: MessageSchema,
        },
        summary: 'The demo protected read: user + tenant + owner flag, resolved from the session alone.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=auth.js.map